import React from 'react';
import { createPortal } from 'react-dom';

import { Button } from 'components/ui/button';

// Matches the primary navigation, which is fixed to the bottom of the screen below Tailwind's lg breakpoint.
const BOTTOM_NAVIGATION_QUERY = '(max-width: 1023.98px)';

export type FloatingSaveBarProps = {
  /** True when there is anything to save or discard. */
  dirty: boolean;
  saving?: boolean;
  /** Save stays disabled while false (for example, invalid input). Defaults to `dirty`. */
  canSave?: boolean;
  onDiscard?: () => void;
  /** Usually opens a review dialog; nothing should be saved until the author confirms there. */
  onSave: () => void;
  saveLabel?: string;
  /** Element whose visibility controls the bar on small screens (the editor). Defaults to the bar's own area. */
  watchRef?: React.RefObject<HTMLElement>;
  testId?: string;
};

/**
 * The admin editors' floating save bar (same look as the Question Library's): unsaved state, Discard, and Save.
 * From lg it is sticky at the bottom of its container. Below lg it is fixed just above the bottom navigation,
 * portalled to <body> because frosted cards' backdrop-filter would otherwise contain a fixed element, and shown
 * only while the watched editor is on screen.
 */
export const FloatingSaveBar: React.FC<FloatingSaveBarProps> = ({
  dirty,
  saving = false,
  canSave,
  onDiscard,
  onSave,
  saveLabel = 'Save',
  watchRef,
  testId = 'floating-save-bar',
}) => {
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [onScreen, setOnScreen] = React.useState(true);
  const [bottomNavShown, setBottomNavShown] = React.useState(
    () => typeof window.matchMedia === 'function' && window.matchMedia(BOTTOM_NAVIGATION_QUERY).matches,
  );

  React.useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const query = window.matchMedia(BOTTOM_NAVIGATION_QUERY);
    const handleChange = () => setBottomNavShown(query.matches);
    handleChange();
    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }, []);

  React.useEffect(() => {
    const node = watchRef?.current ?? anchorRef.current?.parentElement;
    if (!node || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setOnScreen(entry.isIntersecting),
      // The bottom 128px (8rem) of the viewport is the bar and the navigation; the editor must show above them.
      { rootMargin: '0px 0px -128px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [watchRef]);

  const saveEnabled = (canSave ?? dirty) && !saving;
  const pill = (
    <div
      className={`pointer-events-auto flex items-center gap-3 rounded-full py-1.5 pl-5 pr-1.5 text-sm shadow-lg transition-colors ${
        dirty ? 'bg-foundation-base-darkbase-dark-500 text-white' : 'border border-foundation-calm-blue-200 bg-white text-muted-foreground'
      }`}
      aria-live="polite"
    >
      {dirty ? <span className="h-2 w-2 rounded-full bg-amber-400" aria-hidden="true" /> : null}
      <span>{saving ? 'Saving…' : dirty ? 'Unsaved changes' : 'No unsaved changes'}</span>
      {dirty && onDiscard ? (
        <Button
          type="button"
          variant="ghost"
          size="auto"
          className="px-3 py-1.5 text-white hover:text-white/80"
          onClick={onDiscard}
          disabled={saving}
        >
          Discard
        </Button>
      ) : null}
      <Button
        type="button"
        size="auto"
        className={`px-4 py-1.5 disabled:bg-slate-200 disabled:text-slate-500 disabled:opacity-100 ${dirty ? 'bg-white text-foundation-base-darkbase-dark-700 hover:bg-white/90' : ''}`}
        onClick={onSave}
        disabled={!saveEnabled}
      >
        {saving ? 'Saving…' : saveLabel}
      </Button>
    </div>
  );

  if (bottomNavShown) {
    return (
      <>
        <div ref={anchorRef} className="h-16" aria-hidden="true" />
        {onScreen
          ? createPortal(
            <div
              className="pointer-events-none fixed inset-x-0 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] z-50 flex justify-center px-4"
              data-testid={testId}
            >
              {pill}
            </div>,
            document.body,
          )
          : null}
      </>
    );
  }
  return (
    <div ref={anchorRef} className="pointer-events-none sticky bottom-4 z-20 flex justify-center pt-4" data-testid={testId}>
      {pill}
    </div>
  );
};

export default FloatingSaveBar;
